// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import SwiperCore, { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperClass from "swiper/types/swiper-class";
/* eslint-enable spire/fenced-imports */
import { color, pickMultiple } from "@hardwoods/XDL/Utilities";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import React, { FC, isValidElement, useEffect, useState } from "react";
import styled, { css } from "styled-components";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]);
export const CarouselWrapper = styled(StyledWrapper)<{
    navigationOutSide?: boolean;
    mainContainerStyle?: InjectableCss;
    swiperSlideStyle?: InjectableCss;
    centerContent?: boolean;
}>`
    width: 100%;
    max-width: 100%;
    ${(props: any) =>
        props.navigationOutSide
            ? css`
                  position: relative;
              `
            : css`
                  .arrow-button {
                      position: static;
                      transform: none;
                  }
              `}

    &.arrow-responsive {
        ${(props: any) => {
            const { maxWidths, paddingValues } = props.theme.breakpoints || baseTheme.breakpoints;
            // max width for container - padding
            let screenSizeBreakpoint = maxWidths[4] - (paddingValues ? paddingValues[4] * 2 : 20 * 2); // 1280px
            // 40: icon size, 20: margin left/right
            screenSizeBreakpoint += 40 * 2 + 20 * 2; // 1280px
            return (
                props.navigationOutSide &&
                css`
                    ${xScreen.lte(screenSizeBreakpoint)} {
                        margin: 0 auto;
                        max-width: calc(100% - 80px);
                    }
                `
            );
        }}
    }

    .arrow-button {
        &.arrow-disabled {
            ${IconWrapper} {
                color: ${color("text.main", 0.5)};
            }
        }
    }

    &.hide-arrow-mobile {
        ${xScreen.lte("xs")} {
            max-width: 100%;

            .arrow-button {
                display: none;
            }
        }
    }

    .swiper-container-horizontal > .swiper-pagination-bullets {
        bottom: 10px;
        left: 10px;
    }
    .swiper-pagination {
        position: relative;
        text-align: center;
        .swiper-pagination-bullet {
            width: 10px;
            height: 10px;
            margin: 0 5px;
            border: 1px solid ${color("common.ui13")};
            background: transparent;
            opacity: 1;
            &:hover {
                background: ${color("primary.hover")};
            }
            &.swiper-pagination-bullet-active {
                background: ${color("primary.dark")};
            }
        }
    }

    .swiper-container {
        & > div.swiper-button-prev,
        & > div.swiper-button-next {
            display: none;
        }
    }

    .xng-pagination-container {
        padding: 30px 0;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        > button {
            margin-right: 20px;
            &:last-child {
                margin-right: 0;
            }
        }
        > div {
            margin-right: 20px;
        }
    }

    &.arrow-circle {
        .carousel-left-button,
        .carousel-right-button {
            background-color: transparent;

            ${IconWrapper} {
                color: ${color("common.ui12")};
                &,
                & svg {
                    width: 25px;
                    height: 25px;
                }
            }
            &:not(.arrow-disabled):hover ${IconWrapper} {
                color: ${color("text")};
            }

            // disabled
            &.arrow-disabled ${IconWrapper} {
                color: ${color("common.ui12", 0.33)};
            }
        }
    }

    ${injectCss}
`;
export interface SwiperStyle {
    mainContainer?: InjectableCss;
    swiperSlideStyle?: InjectableCss;
    prevArrowSwiper?: ButtonPresentationProps;
    nextArrowSwiper?: ButtonPresentationProps;
    prevArrowSwiperIcon?: ButtonIconPresentationProps;
    nextArrowSwiperIcon?: ButtonIconPresentationProps;
}
export type CarouselProps = Swiper & {
    carouselWrapperClassName?: string;
    extendedStyles?: SwiperStyle;
    navigationOutSide?: boolean;
    customController?: SwiperClass[];
};
const ArrowNavigation: FC<
    ButtonProps & {
        position: "left" | "right" | "top" | "bottom";
        shouldHideArrow?: boolean;
        buttonIconProps?: ButtonIconPresentationProps;
    }
> = ({ position, shouldHideArrow, buttonIconProps, className, ...otherProps }) => {
    const buttonStyle = generateArrowStyle(position);
    let iconSrc = ChevronLeft;
    let iconSize = 40;
    switch (position) {
        case "right":
            iconSrc = ChevronRight;
            break;
        case "top":
            iconSrc = ChevronUp;
            iconSize = 25;
            break;
        case "bottom":
            iconSrc = ChevronDown;
            iconSize = 25;
            break;
    }
    if (shouldHideArrow) {
        return null;
    }
    return (
        <Clickable
            {...buttonStyle}
            {...otherProps}
            className={`arrow-button carousel-${position}-button ${className}  ${
                otherProps.disabled ? "arrow-disabled" : ""
            }`}
        >
            <ButtonIcon
                src={iconSrc}
                size={iconSize}
                className={`carousel-${position}-icon ${otherProps.disabled && "disabled"}`}
                {...buttonIconProps}
            />
        </Clickable>
    );
};

const generateArrowStyle = (position: "left" | "right" | "top" | "bottom") => {
    const factor = position === "left" ? -1 : 1;
    return {
        size: 20,
        css: css`
            position: absolute;
            top: 50%;
            ${position}: -30px;

            ${xScreen.lt(1201)} {
                ${position}: 5px;
            }
            transform: translate(${24 * factor}px, -50%);

            ${(position === "top" || position === "bottom") &&
            css`
                position: relative;
                inset: 0;
                transform: none;
                margin: auto;
            `};

            z-index: 1;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            &:focus {
                box-shadow: none;
            }
            &&& {
                background: transparent;
                border: 0;
                outline: none !important;
            }
        `,
    };
};
const swiperStyle = {
    arrowIconWrapper: {
        size: 40,
        css: css`
            &&& {
                background: transparent;
                border: 0;
                outline: none !important;

                &:focus {
                    box-shadow: none;
                }

                ${IconWrapper} {
                    top: 0;
                    color: ${color("text.secondary")};
                }

                &.arrow-disabled {
                    ${IconWrapper} {
                        color: ${color("text.secondary", 0.25)};
                    }
                }
            }
        `,
    },
};

export const isCarouselArrowVisible = (swiper: SwiperClass) => {
    const numOfSlides = swiper?.slides.length || 0;
    const numOfSlidesPerView = swiper?.params.slidesPerView || 0;
    return numOfSlides > Number(numOfSlidesPerView);
};

const XngCarousel: FC<CarouselProps> = ({
    children,
    navigationOutSide,
    extendedStyles,
    onSwiper: onSwiperProps,
    onSlideChange: onSlideChangeProps,
    customController,
    carouselWrapperClassName,
    ...otherProps
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null);
    const [arrowClass, setArrowClass] = useState<{ next: string; prev: string }>({ next: "", prev: "disabled" });
    const style = mergeToNew(swiperStyle, extendedStyles);
    const [shouldHideArrow, setShouldHideArrow] = useState(true);
    const [centerContent, setCenterContent] = useState(false);

    useEffect(() => {
        const getStyle = async () => {
            // eslint-disable-next-line global-require
            await require("swiper/swiper-bundle.min.css");
        };
        getStyle().then(res => {
            setIsLoaded(true);
        });
    }, []);

    useEffect(() => {
        if (swiperInstance && swiperInstance?.slides) {
            onResizeHandler(swiperInstance);
        }
    }, [swiperInstance?.slides]);

    const shouldHideArrowHandler = (swiper: SwiperClass) => {
        const shouldHideArrow = !isCarouselArrowVisible(swiper);
        setShouldHideArrow(shouldHideArrow);
    };

    const onResizeHandler = (swiper: SwiperClass) => {
        if (!swiper) {
            return;
        }
        const centerContent = (Number(swiper.params.slidesPerView) || 0) > swiper.slides.length;
        setCenterContent(centerContent);
        shouldHideArrowHandler(swiper);
    };

    const moveSlidesForCustomController = (swiper: SwiperClass) => {
        customController?.forEach((item: any) => {
            if ("destroyed" in item) {
                return;
            }

            item?.slideTo(swiper.activeIndex);
        });
    };

    const NextArrowSwiper = (isVertical: boolean) => (
        <ArrowNavigation
            className="xng-carousel-next"
            disabled={!otherProps.loop && arrowClass.next.includes("disabled")}
            shouldHideArrow={shouldHideArrow}
            position={isVertical ? "bottom" : "right"}
            onClick={() => {
                swiperInstance?.slideNext();
            }}
        />
    );

    const PrevArrowSwiper = (isVertical: boolean) => (
        <ArrowNavigation
            className="xng-carousel-prev"
            disabled={!otherProps.loop && arrowClass.prev.includes("disabled")}
            shouldHideArrow={shouldHideArrow}
            position={isVertical ? "top" : "left"}
            onClick={() => {
                swiperInstance?.slidePrev();
            }}
        />
    );

    const detectArrowDisable = (swiper: SwiperClass) => {
        setArrowClass({
            next: `${swiper?.isEnd ? "disabled" : ""}`,
            prev: `${swiper?.isBeginning ? "disabled" : ""}`,
        });
    };

    if (!isLoaded) {
        return null;
    }

    const isVertical = otherProps.direction === "vertical";

    return (
        <CarouselWrapper
            mainContainerStyle={style.mainContainer}
            className={`carousel-main-wrapper ${carouselWrapperClassName}`}
            navigationOutSide={navigationOutSide}
            swiperSlideStyle={style.swiperSlideStyle}
            centerContent={centerContent}
        >
            {navigationOutSide && !otherProps.navigation && PrevArrowSwiper(isVertical)}
            <Swiper
                watchOverflow={true}
                updateOnWindowResize={true}
                centerInsufficientSlides={true}
                {...otherProps}
                onSwiper={(swiper: SwiperClass) => {
                    detectArrowDisable(swiper);
                    setSwiperInstance(swiper);
                    onResizeHandler(swiper);
                    onSwiperProps && onSwiperProps(swiper);
                }}
                onUpdate={(swiper: SwiperClass) => {
                    detectArrowDisable(swiper);
                }}
                onSlideChange={(swiper: SwiperClass) => {
                    detectArrowDisable(swiper);
                    customController && moveSlidesForCustomController(swiper);
                    onSlideChangeProps && onSlideChangeProps(swiper);
                }}
                onResize={(swiper: SwiperClass) => {
                    onResizeHandler(swiper);
                    detectArrowDisable(swiper);
                }}
                onReachBeginning={() => {
                    setArrowClass({
                        next: "",
                        prev: "disabled",
                    });
                }}
                onReachEnd={() => {
                    setArrowClass({
                        next: "disabled",
                        prev: "",
                    });
                }}
            >
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }
                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const xngKeys = ["renderWithoutSwiperSlide"];
                    const childrenProps = omitMultiple(props, [...swiperSlidePropsKeys, ...xngKeys]);
                    const swiperSlideProps = pickMultiple(props, swiperSlidePropsKeys);
                    if (props.renderWithoutSwiperSlide) {
                        return child;
                    }
                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                        </SwiperSlide>
                    );
                })}

                {(otherProps.pagination || otherProps.navigation) && (
                    <StyledWrapper className="xng-pagination-container">
                        {otherProps.navigation && PrevArrowSwiper(isVertical)}
                        {otherProps.pagination && <div className="swiper-pagination" />}
                        {otherProps.navigation && NextArrowSwiper(isVertical)}
                    </StyledWrapper>
                )}
                {otherProps.scrollbar && <div className="swiper-scrollbar" />}
            </Swiper>
            {navigationOutSide && !otherProps.navigation && NextArrowSwiper(isVertical)}
        </CarouselWrapper>
    );
};

export default XngCarousel;
